import React, { useState, useEffect, useRef } from 'react';
import { Search, X, BookOpen, GraduationCap, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Tokens matching the premium SWU aesthetic
const D = '#0a0a14';
const G = '#d4a843';
const BR = 'rgba(255,255,255,0.07)';
const TX = 'rgba(255,255,255,0.88)';
const MU = 'rgba(255,255,255,0.45)';

interface Degree {
  slug: string;
  label: string;
  collegeLabel: string;
  collegeId: string;
}

// Flat list of all degrees for search
const ALL_DEGREES: Degree[] = [
  // Tech
  { slug: 'uni-software-development', label: 'BSc Software Development & Engineering', collegeLabel: 'Technology & AI', collegeId: 'tech' },
  { slug: 'uni-ai-machine-learning', label: 'BSc Artificial Intelligence & ML', collegeLabel: 'Technology & AI', collegeId: 'tech' },
  { slug: 'uni-cybersecurity', label: 'BSc Cybersecurity & Digital Defence', collegeLabel: 'Technology & AI', collegeId: 'tech' },
  { slug: 'uni-data-science', label: 'BSc Data Science & Analytics', collegeLabel: 'Technology & AI', collegeId: 'tech' },
  { slug: 'uni-cloud-devops', label: 'BSc Cloud Computing & DevOps', collegeLabel: 'Technology & AI', collegeId: 'tech' },
  // Biz
  { slug: 'uni-business-administration', label: 'BSc Business Administration & Strategy', collegeLabel: 'Business & Entrepreneurship', collegeId: 'biz' },
  { slug: 'uni-digital-marketing', label: 'BSc Digital Marketing & Growth', collegeLabel: 'Business & Entrepreneurship', collegeId: 'biz' },
  { slug: 'uni-finance-investment', label: 'BSc Finance & Investment', collegeLabel: 'Business & Entrepreneurship', collegeId: 'biz' },
  { slug: 'uni-leadership-management', label: 'BSc Leadership & Management', collegeLabel: 'Business & Entrepreneurship', collegeId: 'biz' },
  { slug: 'uni-entrepreneurship-startup', label: 'BSc Entrepreneurship & Startup', collegeLabel: 'Business & Entrepreneurship', collegeId: 'biz' },
  // Health
  { slug: 'uni-psychology', label: 'BSc Psychology & Human Behaviour', collegeLabel: 'Health & Human Sciences', collegeId: 'health' },
  { slug: 'uni-nutrition-health', label: 'BSc Nutrition & Health Science', collegeLabel: 'Health & Human Sciences', collegeId: 'health' },
  { slug: 'uni-mental-health-counselling', label: 'BSc Mental Health & Counselling', collegeLabel: 'Health & Human Sciences', collegeId: 'health' },
  { slug: 'uni-health-administration', label: 'BSc Health Administration & Management', collegeLabel: 'Health & Human Sciences', collegeId: 'health' },
  { slug: 'uni-aged-care-gerontology', label: 'BSc Aged Care & Gerontology', collegeLabel: 'Health & Human Sciences', collegeId: 'health' },
  // Arts
  { slug: 'uni-graphic-design', label: 'BSc Graphic Design & Visual Communication', collegeLabel: 'Creative Arts & Media', collegeId: 'arts' },
  { slug: 'uni-creative-writing', label: 'BSc Creative Writing & Storytelling', collegeLabel: 'Creative Arts & Media', collegeId: 'arts' },
  { slug: 'uni-film-content-creation', label: 'BSc Film, Video & Content Creation', collegeLabel: 'Creative Arts & Media', collegeId: 'arts' },
  { slug: 'uni-music-production', label: 'BSc Music Production & Audio', collegeLabel: 'Creative Arts & Media', collegeId: 'arts' },
  // Science
  { slug: 'uni-climate-science', label: 'BSc Climate Science & Sustainability', collegeLabel: 'Science & Environment', collegeId: 'sci' },
  { slug: 'uni-advanced-biology', label: 'BSc Advanced Biology & Life Sciences', collegeLabel: 'Science & Environment', collegeId: 'sci' },
  { slug: 'uni-physics-applied-maths', label: 'BSc Physics & Applied Mathematics', collegeLabel: 'Science & Environment', collegeId: 'sci' },
  { slug: 'uni-environmental-science', label: 'BSc Environmental Science & Ecology', collegeLabel: 'Science & Environment', collegeId: 'sci' },
  // Hum
  { slug: 'uni-philosophy-ethics', label: 'BSc Philosophy & Ethics', collegeLabel: 'Humanities & Philosophy', collegeId: 'hum' },
  { slug: 'uni-world-history', label: 'BSc World History & Civilisations', collegeLabel: 'Humanities & Philosophy', collegeId: 'hum' },
  { slug: 'uni-sociology', label: 'BSc Sociology & Social Theory', collegeLabel: 'Humanities & Philosophy', collegeId: 'hum' },
  { slug: 'uni-political-science', label: 'BSc Political Science & Int\'l Relations', collegeLabel: 'Humanities & Philosophy', collegeId: 'hum' },
  { slug: 'uni-law-legal-literacy', label: 'BSc Law & Legal Literacy', collegeLabel: 'Humanities & Philosophy', collegeId: 'hum' },
  // Lang
  { slug: 'uni-academic-writing', label: 'BSc Academic Writing & Research', collegeLabel: 'Languages & Communication', collegeId: 'lang' },
  { slug: 'uni-public-speaking', label: 'BSc Public Speaking & Presentation', collegeLabel: 'Languages & Communication', collegeId: 'lang' },
  { slug: 'uni-linguistics', label: 'BSc Linguistics & Language Science', collegeLabel: 'Languages & Communication', collegeId: 'lang' },
  { slug: 'uni-cross-cultural-communication', label: 'BSc Cross-Cultural Communication', collegeLabel: 'Languages & Communication', collegeId: 'lang' },
  // Edu
  { slug: 'uni-teaching-instructional-design', label: 'BSc Teaching & Instructional Design', collegeLabel: 'Education & Coaching', collegeId: 'edu' },
  { slug: 'uni-life-coaching', label: 'BSc Life Coaching & Personal Development', collegeLabel: 'Education & Coaching', collegeId: 'edu' },
  { slug: 'uni-early-childhood-education', label: 'BSc Early Childhood Education', collegeLabel: 'Education & Coaching', collegeId: 'edu' },
  { slug: 'uni-corporate-training', label: 'BSc Corporate Training & L&D', collegeLabel: 'Education & Coaching', collegeId: 'edu' },
  // Faith
  { slug: 'uni-biblical-studies-hermeneutics', label: 'BSc Biblical Studies & Hermeneutics', collegeLabel: 'Faith & Spiritual Studies', collegeId: 'faith' },
  { slug: 'uni-systematic-theology', label: 'BSc Systematic Theology', collegeLabel: 'Faith & Spiritual Studies', collegeId: 'faith' },
  { slug: 'uni-world-religions-comparative-theology', label: 'BSc World Religions & Comparative Theology', collegeLabel: 'Faith & Spiritual Studies', collegeId: 'faith' },
  { slug: 'uni-christian-ethics-apologetics', label: 'BSc Christian Ethics & Apologetics', collegeLabel: 'Faith & Spiritual Studies', collegeId: 'faith' },
  { slug: 'uni-practical-ministry-pastoral-theology', label: 'BSc Practical Ministry & Pastoral Theology', collegeLabel: 'Faith & Spiritual Studies', collegeId: 'faith' },
  // Mastery
  { slug: 'uni-productivity-mastery', label: 'BSc Productivity, Focus & Time Mastery', collegeLabel: 'Personal Mastery', collegeId: 'mastery' },
  { slug: 'uni-financial-literacy', label: 'BSc Financial Literacy for Life', collegeLabel: 'Personal Mastery', collegeId: 'mastery' },
  { slug: 'uni-emotional-intelligence', label: 'BSc Emotional Intelligence & Resilience', collegeLabel: 'Personal Mastery', collegeId: 'mastery' },
  { slug: 'uni-mindfulness-wellbeing', label: 'BSc Mindfulness, Meditation & Wellbeing', collegeLabel: 'Personal Mastery', collegeId: 'mastery' },
  // Prep
  { slug: 'uni-critical-thinking', label: 'BSc Critical Thinking & Logic', collegeLabel: 'University Prep', collegeId: 'prep' },
  { slug: 'uni-academic-research', label: 'BSc Academic Research & Writing', collegeLabel: 'University Prep', collegeId: 'prep' },
  { slug: 'uni-career-exploration', label: 'BSc Career Exploration & Skills', collegeLabel: 'University Prep', collegeId: 'prep' },
  { slug: 'uni-study-strategies', label: 'BSc Advanced Study Strategies', collegeLabel: 'University Prep', collegeId: 'prep' },
];

export const CourseSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Degree[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }
    const filtered = ALL_DEGREES.filter(d => 
      d.label.toLowerCase().includes(query.toLowerCase()) || 
      d.collegeLabel.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5);
    setResults(filtered);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (slug: string) => {
    setIsOpen(false);
    setQuery('');
    navigate(`/university/class/${slug}`);
  };

  return (
    <div ref={searchRef} style={{ position: 'relative', width: '100%', maxWidth: 500 }}>
      {/* Search Input */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Search 
          size={18} 
          style={{ 
            position: 'absolute', 
            left: 16, 
            color: query ? G : MU, 
            transition: 'color 0.2s' 
          }} 
        />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={t('dashboard.searchPlaceholder', 'Search for courses, degrees, or subjects...')}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${isOpen ? 'rgba(212,168,67,0.4)' : BR}`,
            borderRadius: 16,
            padding: '14px 48px 14px 44px',
            color: TX,
            fontSize: 14,
            fontWeight: 500,
            outline: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isOpen ? '0 8px 16px rgba(0,0,0,0.4)' : 'none'
          }}
        />
        <AnimatePresence>
          {query && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => { setQuery(''); setResults([]); }}
              style={{
                position: 'absolute',
                right: 12,
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: MU
              }}
            >
              <X size={14} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Results Dropdown */}
      <AnimatePresence>
        {isOpen && query.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              background: D,
              border: `1px solid ${BR}`,
              borderRadius: 20,
              boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
              overflow: 'hidden',
              zIndex: 1000,
              padding: '8px'
            }}
          >
            {results.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <p style={{ fontSize: 11, color: MU, fontWeight: 700, letterSpacing: '1px', padding: '12px 16px 8px' }}>
                  {results.length} {t('dashboard.searchResults', 'PROGRAMS FOUND')}
                </p>
                {results.map((res, i) => (
                  <motion.button
                    key={res.slug}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => handleSelect(res.slug)}
                    whileHover={{ background: 'rgba(255,255,255,0.03)', x: 4 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      padding: '12px 16px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      borderRadius: 12,
                      width: '100%',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: 'rgba(212,168,67,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: G,
                      flexShrink: 0
                    }}>
                      <BookOpen size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {res.label}
                      </h4>
                      <p style={{ margin: 2, fontSize: 12, color: MU, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <GraduationCap size={12} /> {res.collegeLabel}
                      </p>
                    </div>
                    <ChevronRight size={18} color={MU} />
                  </motion.button>
                ))}
                
                <div style={{ borderTop: `1px solid ${BR}`, margin: '8px 0', padding: '12px 16px' }}>
                  <button 
                    onClick={() => navigate('/university/enrol')}
                    style={{ background: 'none', border: 'none', color: G, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                  >
                    {t('dashboard.browseAll', 'View All Degrees →')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                <p style={{ margin: 0, color: MU, fontSize: 14 }}>
                  {t('dashboard.noResults', 'No courses matching')} "{query}"
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
